'use client';

import { forwardRef, Ref, useEffect, useRef } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { IconSearch } from '@tabler/icons-react';
import { useDebounceFn } from 'ahooks';
import { Autocomplete, ComboboxStringData, useMatches } from '@mantine/core';
import { useMounted } from '@mantine/hooks';
import { ESearchType } from '../../../enums/search-type';
import { useGlobalSearchStore } from '../../../stores/use-global-search-store';

interface ISearchInputProps {
  onClose?: () => void;
}

interface ISearchOption {
  label: string;
  type: ESearchType;
  href: string;
}

export const SearchInput = forwardRef(
  ({ onClose }: ISearchInputProps, ref?: Ref<HTMLInputElement>) => {
    const router = useRouter();
    const { outletId } = useParams<{ outletId?: string }>();
    const searchParams = useSearchParams();
    const isMounted = useMounted();
    const isMobileView = useMatches({ base: true, md: false });

    const hasSubmittedRef = useRef(false);

    const query = useGlobalSearchStore((s) => s.query);
    const setQuery = useGlobalSearchStore((s) => s.setQuery);
    const resetSearch = useGlobalSearchStore((s) => s.resetState);
    const selectedSearchType = useGlobalSearchStore((s) => s.type);
    const setSelectedSearchType = useGlobalSearchStore((s) => s.setType);

    const currentSearch = searchParams.get('search') || '';
    const currentType = searchParams.get('type');

    const hasQuery = query.trim() !== '';

    const options: ISearchOption[] = [
      ...(outletId
        ? [
            {
              label: `Cari "${query}" di Toko ini`,
              type: ESearchType.OutletDetail,
              href: `/outlets/${outletId}`,
            },
          ]
        : []),
      {
        label: `Cari "${query}" di Produk Kesehatan`,
        type: ESearchType.Product,
        href: '/produk-kesehatan',
      },
      {
        label: `Cari "${query}" di Toko Kesehatan`,
        type: ESearchType.Outlet,
        href: '/produk-kesehatan',
      },
      {
        label: `Cari "${query}" di Layanan Kesehatan`,
        type: ESearchType.Service,
        href: '/produk-kesehatan',
      },
    ];

    const autoCompleteOptions: ComboboxStringData = options.map((o) => ({
      value: o.label,
    }));

    const buildSearchParams = (query: string, type?: ESearchType) => {
      const sp = new URLSearchParams();
      sp.set('search', query);
      if (type && type !== ESearchType.OutletDetail) {
        sp.set('type', type);
      }
      return sp.toString();
    };

    const setSearchAndType = () => {
      if (!query.trim()) {
        return;
      }

      const option = options.find((o) => o.type === selectedSearchType);
      if (!option) {
        return;
      }

      router.push(`${option.href}?${buildSearchParams(query, option.type)}`);
    };

    const { run: runSearch, cancel: cancelSearch } = useDebounceFn(
      setSearchAndType,
      { wait: 500 }
    );

    const onOptionSubmit = (selectedValue: string) => {
      const option = options.find((o) => o.label === selectedValue);
      if (!option) {
        return;
      }

      hasSubmittedRef.current = true;
      setSelectedSearchType(option.type);

      router.push(`${option.href}?${buildSearchParams(query, option.type)}`);

      if (isMobileView) {
        onClose?.();
      }
    };

    const onChange = (value: string) => {
      if (hasSubmittedRef.current) {
        hasSubmittedRef.current = false;
        return;
      }

      setQuery(value);

      if (currentSearch) {
        cancelSearch();
        runSearch();
      }
    };

    const onClear = () => {
      cancelSearch();
      resetSearch();

      const params = new URLSearchParams();
      if (currentType) {
        params.set('type', currentType);
      }

      router.replace(
        `${window.location.pathname}${params.toString() ? `?${params}` : ''}`
      );

      if (isMobileView) {
        setTimeout(() => onClose?.(), 0);
      }
    };

    useEffect(() => {
      setQuery(currentSearch);

      if (
        currentType &&
        Object.values(ESearchType).includes(currentType as ESearchType)
      ) {
        setSelectedSearchType(currentType as ESearchType);
      }
    }, [currentSearch, currentType]);

    if (!isMounted) {
      return null;
    }

    return (
      <Autocomplete
        ref={ref}
        w="100%"
        data={hasQuery ? autoCompleteOptions : []}
        placeholder="Cari di Marketplace"
        leftSection={<IconSearch size={20} stroke={1.5} />}
        inputMode="search"
        radius="md"
        classNames={{
          input: 'py-5! placeholder:text-gray-600! pl-10!',
        }}
        value={query}
        onChange={onChange}
        onClear={onClear}
        clearable
        onOptionSubmit={onOptionSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && hasQuery) {
            e.preventDefault();

            const firstOption = options[0];
            if (!firstOption) {
              return;
            }

            hasSubmittedRef.current = true;
            setSelectedSearchType(firstOption.type);
            router.push(
              `${firstOption.href}?${buildSearchParams(query, firstOption.type)}`
            );
            if (isMobileView) {
              setTimeout(() => onClose?.(), 0);
            }
          }
        }}
      />
    );
  }
);
